import * as React from "react"
import {
    MessageSquare,
    Home,
    Users,
    Globe,
    Image,
    ImagePlus,
    Scissors,
    Edit,
    Text,
    FileText,
    Music,
    Utensils,
    File,
    History,
    DollarSign,
    ArrowLeft,
    ChevronRight,
    AudioLines,
    TextIcon,
    AudioWaveformIcon,
    VoicemailIcon,
    ArrowLeft as BackIcon,
    Crown, Pencil, Trash
} from "lucide-react"

import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarRail,
    SidebarMenu,
    SidebarMenuItem,
    SidebarMenuButton,
    SidebarMenuSub,
    SidebarMenuSubItem,
    SidebarMenuSubButton, useSidebar,
} from "@/Components/ui/sidebar.jsx"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/Components/ui/collapsible.jsx"
import { ScrollArea } from "@/Components/ui/scroll-area.jsx"
import { Button } from "@/Components/ui/button.jsx"
import { NavUser } from "@/Components/New/SidebarUser.jsx"
import { motion, AnimatePresence } from "framer-motion"
import { AuthContext } from "../../Contexts/AuthProvider.jsx"
import { API_URL } from "@/index.jsx"
import {useNavigate} from "react-router-dom";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import useSWR from "use-swr";
import axios from "axios";
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from "@/components/ui/context-menu"
import { Input } from "@/Components/ui/input.jsx"
import {useTimeout} from "@mantine/hooks";
import {
    Badge,
    Dialog, DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/Components/ui/index.js";
import {useIsMobile} from "@/hooks/use-mobile.jsx";
import {toast} from "sonner";

function ChatListSidebar({ onBack }) {
    const { token } = useContext(AuthContext)
    const navigate = useNavigate()
    const [chats, setChats] = useState([])
    const [loading, setLoading] = useState(true)
    const [editingId, setEditingId] = useState(null)
    const [editingValue, setEditingValue] = useState("")
    const intervalRef = useRef(null)
    const saveTimeout = useRef(null)
    const [deleteId, setDeleteId] = useState(null)

    const fetchedOnce = useRef(false)
    const {isMobile, setOpenMobile} = useSidebar();

    const fetchChats = async () => {
        if (!token) return
        try {
            const res = await axios.get(`${API_URL}/chat/list`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            })
            if (editingId == null) {
                const newChats = res.data?.threads.filter(chat => chat.route === "/chat") || []
                if (JSON.stringify(newChats) !== JSON.stringify(chats)) {
                    setChats(newChats)
                }
                fetchedOnce.current = true
                setLoading(false)
            }
        } catch (e) {
            if (!fetchedOnce.current) setLoading(false)
        }
    }

    const handleDeleteChat = async (id) => {
        if (editingId === id) return

        try {
            await axios.delete(`${API_URL}/chat/threads/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            setChats(prev => prev.filter(chat => chat.id !== id))
            toast({
                title: "Chat smazán",
                description: "Chat byl úspěšně odstraněn.",
                variant: "success"
            })
            // Pokud jsme na detailu mazaného chatu, přesměruj
            if (window.location.pathname === `/chat/${id}`) {
                navigate("/chat")
                if(isMobile) setOpenMobile(false)
            }
        } catch (e) {
            toast({
                title: "Chyba při mazání chatu",
                description: e?.response?.data?.error || "Něco se pokazilo, zkuste to znovu.",
                variant: "destructive"
            })
        }
        setDeleteId(null)
    }

    useEffect(() => {
        setLoading(true)
        fetchChats()
        intervalRef.current = setInterval(() => {
            if (editingId == null) fetchChats()
        }, 10000)
        return () => clearInterval(intervalRef.current)
        // eslint-disable-next-line
    }, [token, editingId])

    const startEdit = (id, name) => {
        setEditingId(id)
        setEditingValue(name)
    }

    const saveEdit = (id) => {
        const name = editingValue.trim();
        if (!name || editingId !== id) {
            setEditingId(null);
            setEditingValue("");
            return;
        }
        setChats(prev =>
            prev.map(chat =>
                chat.id === id ? { ...chat, name } : chat
            )
        );
        setEditingId(null);
        setEditingValue("");
        clearTimeout(saveTimeout.current);

        saveTimeout.current = setTimeout(async () => {
            try {
                await axios.post(`${API_URL}/chat/rename`,
                    {
                        chatId: id,
                        name
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json"
                        }
                    })
                setTimeout(() => {
                    fetchChats();
                }, 2000);
            } catch (e) {
            }
        }, 10);
    }

    const handleChatClick = (id) => {
        if (editingId !== id) {
            navigate(`/chat/${id}`)
            if(isMobile) {
                setOpenMobile(false)
            }
            fetchChats();
        }
    }

    const handleNewChat = () => {
        navigate("/chat")
        if(isMobile) {
            setOpenMobile(false)
        }
    }

    const groupedChats = useMemo(() => {
        const groups = {};
        chats.forEach((chat) => {
            const dateStr = chat.created_at || chat.createdAt;
            if (!dateStr) return;
            const date = new Date(dateStr);
            const key = date.toDateString();
            if (!groups[key]) groups[key] = [];
            groups[key].push(chat);
        });
        return Object.keys(groups)
            .sort((a, b) => new Date(b) - new Date(a))
            .map((key) => ({ date: new Date(key), chats: groups[key] }));
    }, [chats]);

    function formatDate(date) {
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        if (
            date.toDateString() === today.toDateString()
        ) return "Dnes";
        if (
            date.toDateString() === yesterday.toDateString()
        ) return "Včera";
        return date.toLocaleDateString("cs-CZ", { weekday: "long", day: "numeric", month: "long" });
    }

    return (
        <div className="flex flex-col h-full px-4">
            <div className="flex items-center gap-2 mb-4">
                <Button variant="ghost" size="icon" onClick={onBack}>
                    <BackIcon className="w-5 h-5" />
                </Button>
                <span className="font-semibold text-lg">Chaty</span>
            </div>
            <Button variant="outline" className="mb-4" onClick={handleNewChat}>
                Nový chat
            </Button>
            <ScrollArea className="flex-1 pr-2 h-full">
                {!fetchedOnce.current && loading ? (
                    <div className="text-muted-foreground">Načítání...</div>
                ) : (
                    groupedChats.length === 0 ? (
                        <div className="text-muted-foreground">Žádné chaty</div>
                    ) : (
                        groupedChats.map(group => (
                            <div key={group.date.toISOString()} className="mb-3">
                                <div className="text-xs font-semibold text-secondary-foreground mb-2 mt-2 select-none">
                                    {formatDate(group.date)}
                                </div>
                                {group.chats.map(chat => {
                                    const isActive = window.location.pathname === `/chat/${chat.id}`
                                    const isEditing = editingId === chat.id

                                    return (
                                        <ContextMenu key={chat.id}>
                                            <ContextMenuTrigger asChild>
                                                <div
                                                    className={
                                                        "p-2 rounded-md max-w-full cursor-pointer mb-1 relative hover:bg-accent " +
                                                        (isActive ? "bg-accent" : "")
                                                    }
                                                    style={{ minWidth: 0 }}
                                                    onDoubleClick={() => startEdit(chat.id, chat.name)}
                                                    onClick={() => handleChatClick(chat.id)}
                                                >
                                                    {chat.changed === 1 && (
                                                        <div
                                                            className="absolute top-1 left-1 rounded-full bg-blue-500 w-2 h-2"
                                                        >
                                                        </div>
                                                    )}

                                                    {isEditing ? (
                                                        <form
                                                            onSubmit={e => {
                                                                e.preventDefault()
                                                                saveEdit(chat.id)
                                                            }}
                                                            style={{ width: "100%" }}
                                                        >
                                                            <Input
                                                                autoFocus
                                                                value={editingValue}
                                                                onChange={e => setEditingValue(e.target.value)}
                                                                onKeyDown={e => {
                                                                    if (e.key === "Escape") {
                                                                        setEditingId(null)
                                                                        setEditingValue("")
                                                                    }
                                                                    if (e.key === "Enter") {
                                                                        saveEdit(chat.id)
                                                                    }
                                                                }}
                                                                onBlur={() => saveEdit(chat.id)}
                                                                className="px-0 py-0 border-0 focus:outline-none focus:border-none text-sm w-full"
                                                            />
                                                        </form>
                                                    ) : (
                                                        <span className="block max-w-full text-ellipsis truncate text-sm">
                                                            {chat.name}
                                                        </span>
                                                    )}
                                                </div>
                                            </ContextMenuTrigger>
                                            <ContextMenuContent className="w-48">
                                                <ContextMenuItem onClick={() => startEdit(chat.id, chat.name)}>
                                                    <Pencil className="w-4 h-4 mr-2" />
                                                    Přejmenovat
                                                </ContextMenuItem>
                                                <ContextMenuItem
                                                    variant="destructive"
                                                    onClick={() => setDeleteId(chat.id)}
                                                    disabled={editingId === chat.id}
                                                >
                                                    <Trash className="w-4 h-4 mr-2" />
                                                    Smazat
                                                </ContextMenuItem>
                                            </ContextMenuContent>
                                        </ContextMenu>
                                    )
                                })}
                            </div>
                        ))
                    )
                )}
            </ScrollArea>
            <Dialog open={!!deleteId} onOpenChange={open => !open && setDeleteId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Opravdu smazat tento chat?</DialogTitle>
                        <DialogDescription>
                            Tato akce je nevratná. Všechny zprávy v chatu budou odstraněny.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="secondary">Zrušit</Button>
                        </DialogClose>
                        <Button
                            variant="destructive"
                            onClick={() => handleDeleteChat(deleteId)}
                            disabled={editingId === deleteId}
                        >
                            Smazat
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </div>
    )
}

export function AppSidebar(props) {
    const [showChatList, setShowChatList] = React.useState(false)
    const navigate = useNavigate()
    const { user } = useContext(AuthContext) // Get user from context

    // Check if user has active subscription
    const hasActiveSubscription = user?.active && (user.active === "active" || user.active === "trialing")

    // Helper function to check if a parent item should show crown (all children are premium)
    const shouldShowCrown = (item) => {
        if (!item.children) return item.premium && !hasActiveSubscription

        // If it has children, check if ALL children are premium
        const allChildrenPremium = item.children.every(child => child.premium)
        return allChildrenPremium && !hasActiveSubscription
    }

    // Helper function to check if collapsible should be disabled
    const shouldDisableCollapsible = (item) => {
        if (!item.children) return false

        // Disable if ALL children are premium and user has no subscription
        const allChildrenPremium = item.children.every(child => child.premium)
        return allChildrenPremium && !hasActiveSubscription
    }

    // Helper function to handle premium feature clicks
    const handlePremiumFeatureClick = (e, url) => {
        e.preventDefault()
        if (!hasActiveSubscription) {
            // Redirect to upgrade page or show upgrade modal
            navigate("/pricing") // or whatever your upgrade route is
            return
        }
        navigate(url)
    }

    // Helper function to handle collapsible premium clicks
    const handleCollapsibleClick = (e, item) => {
        e.preventDefault()
        if (shouldDisableCollapsible(item)) {
            navigate("/pricing")
            return
        }
        // If not disabled, allow normal collapsible behavior by not preventing default
        e.target.click()
    }

    // Sidebar items data
    const menuMain = [
        { label: "Přehled", icon: Home, url: "/overview" },
        {
            label: "Asistenti",
            icon: Users,
            url: "/templates",
            premium: true
        },
        {
            label: "Chat",
            icon: MessageSquare,
            update: true,
            onClick: () => {
                navigate("/chat")
                setShowChatList(true)
            },
        },
        { label: "Překladač", icon: Globe, url: "/translate" },
        {
            label: "Obrázky",
            icon: Image,
            children: [
                { label: "Generátor obrázků", icon: ImagePlus, url: "/images" },
                { label: "Odstranění pozadí", icon: Scissors, url: "/removebg", premium: true },
                { label: "Editor obrázků", icon: Edit, url: "/edit-image", premium: true },
                { label: "AI popis obrázků", icon: Text, url: "/describe", premium: true },
            ],
        },
        { label: "Summarizér", icon: FileText, url: "/summarizer" },
        {
            label: "Audio",
            icon: Music,
            premium: true,
            children: [
                { label: "Tvorba audia", icon: AudioLines, url: "/text-speech", premium: true },
                { label: "Převod na text", icon: TextIcon, url: "/audio/speech-to-text", premium: true },
                { label: "Zvukové efekty", icon: AudioWaveformIcon, url: "/audio/sound-effects", premium: true },
                { label: "Izolování hlasu", icon: VoicemailIcon, url: "/audio/voice-isolation", premium: true },
                { label: "Klonování hlasu", icon: Pencil, url: "/audio/voice-cloning", premium: true, 'new': true },
            ],
        },
        { label: "Vaše soubory", icon: File, url: "/data" },
    ]

    const menuSecondary = [
        { label: "Historie kreditů", icon: History, url: "/history" },
        { label: "Vydělávejte s Coudy AI", icon: DollarSign, url: "/affiliate" },
        { label: "Zpět na domovskou stránku", icon: ArrowLeft, url: "/" },
    ]

    useEffect(() => {
        if(window.location.pathname.startsWith("/chat")) {
            setShowChatList(true)
        } else {
            setShowChatList(false)
        }
    }, []);

    const isActive = (url) => window.location.pathname === url

    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? "100%" : "-100%",
            opacity: 0,
            position: "absolute",
            width: "100%",
            height: "100%",
        }),
        center: {
            x: 0,
            opacity: 1,
            position: "relative",
            width: "100%",
            height: "100%",
        },
        exit: (direction) => ({
            x: direction < 0 ? "100%" : "-100%",
            opacity: 0,
            position: "absolute",
            width: "100%",
            height: "100%",
        }),
    }

    const [direction, setDirection] = React.useState(1)
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar()

    const handleShowChatList = () => {
        setDirection(1)
        setShowChatList(true)
    }
    const handleBack = () => {
        setDirection(-1)
        setShowChatList(false)
    }

    return (
        <Sidebar collapsible="offcanvas" {...props}>
            <SidebarHeader className="ml-2 my-2 mt-3">
                <div className="flex items-center gap-4">
                    <div className={"inline-flex justify-center "}>
                        <div>
                            <svg
                                width="8mm"
                                viewBox="0 0 50.071873 53.3461"
                                version="1.1"
                                id="svg1"
                                xmlns="http://www.w3.org/2000/svg"
                                id="defs1">
                                <g
                                    id="layer1"
                                    transform="translate(-11.989448,-128.97869)">
                                    <defs>
                                        <linearGradient id="gradient1" gradientTransform="rotate(0)">
                                            <stop offset="0%" stop-color="#a855f7"/>
                                            <stop offset="100%" stop-color="#ec4899"/>
                                        </linearGradient>
                                    </defs>
                                    <path
                                        id="rect1-2"
                                        style={{fill: "url(#gradient1)", strokeWidth: 0.264583}}
                                        d="m 42.553242,133.70719 a 6.8079619,6.8079619 0 0 0 -7.432829,6.11938 6.8079619,6.8079619 0 0 0 1.350502,4.7506 l -9.089068,7.95721 a 6.7839479,6.7839479 0 0 0 -4.439262,-2.27439 6.7839479,6.7839479 0 0 0 -4.757241,1.36543 l -7.580492,-8.39579 a 4.5506849,4.5506849 0 0 0 1.0958,-2.53178 4.5506849,4.5506849 0 0 0 -4.0904237,-4.96838 4.5506849,4.5506849 0 0 0 -4.9688981,4.09037 4.5506849,4.5506849 0 0 0 4.0909384,4.96844 4.5506849,4.5506849 0 0 0 3.0454322,-0.8024 l 7.5781702,8.39296 a 6.7839479,6.7839479 0 0 0 -1.819597,3.97869 6.7839479,6.7839479 0 0 0 1.272483,4.64492 l -10.0710558,8.8111 a 6.8079619,6.8079619 0 0 0 -4.123311,-1.92914 6.8079619,6.8079619 0 0 0 -7.4328284,6.11938 6.8079619,6.8079619 0 0 0 6.1193784,7.43283 6.8079619,6.8079619 0 0 0 7.4328284,-6.11938 6.8079619,6.8079619 0 0 0 -1.2664921,-4.65316 l 10.0790045,-8.81812 a 6.7839479,6.7839479 0 0 0 4.08749,1.9184 6.7839479,6.7839479 0 0 0 0.1932,0.005 l -1.3e-4,10.70455 a 5.4676142,5.4676142 0 0 0 -4.678295,4.87396 5.4676142,5.4676142 0 0 0 4.914404,5.96979 5.4676142,5.4676142 0 0 0 5.969847,-4.91492 5.4676142,5.4676142 0 0 0 -4.914969,-5.96933 5.4676142,5.4676142 0 0 0 -0.169855,-0.005 l -6.21e-4,-10.68075 a 6.7839479,6.7839479 0 0 0 3.988067,-1.79666 l 7.760425,6.83387 a 4.5506849,4.5506849 0 0 0 -0.768688,2.10296 4.5506849,4.5506849 0 0 0 4.090424,4.96839 4.5506849,4.5506849 0 0 0 4.968383,-4.09043 4.5506849,4.5506849 0 0 0 -4.090374,-4.9689 4.5506849,4.5506849 0 0 0 -3.465116,1.14177 l -7.748929,-6.82392 a 6.7839479,6.7839479 0 0 0 1.359102,-3.44858 6.7839479,6.7839479 0 0 0 -0.988764,-4.22959 l 9.169019,-8.02681 a 6.8079619,6.8079619 0 0 0 4.018891,1.84945 6.8079619,6.8079619 0 0 0 7.433342,-6.11933 6.8079619,6.8079619 0 0 0 -6.119892,-7.43288 z"
                                        transform="rotate(-5.5356918)"/>
                                </g>
                            </svg>
                        </div>

                        <div className={"dark:hidden block ml-2 mt-1"}>
                            <svg
                                width="20mm"
                                viewBox="0 0 127.22914 42.930099"
                                version="1.1"
                                id="svg1"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <defs
                                    id="defs1"/>
                                <g
                                    id="layer1"
                                    transform="translate(-71.045215,-136.04298)">
                                    <path
                                        id="path5"
                                        style={{opacity: 1, fill: "#000000", strokeWidth: 0.271314}}
                                        d="m 169.55859,136.57227 c 0,3.34114 0,6.68229 0,10.02343 -3.64384,-2.97109 -9.03383,-3.56317 -13.20995,-1.38285 -4.45036,2.14574 -7.22686,7.20497 -6.71021,12.10926 0.37354,4.63585 3.64458,8.85447 8.0242,10.40487 3.89052,1.44749 8.47464,0.73327 11.73539,-1.83816 2.99116,-2.29577 4.80307,-6.02011 4.75408,-9.79171 0.0392,-4.31115 -0.005,-8.62355 0.0139,-12.93539 0,-2.37292 0,-4.74583 0,-7.11875 -1.53581,0 -3.07162,0 -4.60743,0 0,0.17643 0,0.35286 0,0.5293 z m -85.99609,7.14648 c -4.799296,0.20059 -9.491305,3.09438 -11.448599,7.54401 -1.921844,4.05822 -1.187366,9.13683 1.686685,12.56286 3.484823,4.31576 9.776512,5.94694 14.959693,4.04564 2.529658,-0.84345 4.713598,-2.52375 6.325228,-4.63279 0.498008,-0.4781 0.853415,-0.9363 -0.0036,-1.21895 -1.134739,-0.76039 -2.26932,-1.52102 -3.404172,-2.28124 -1.342743,2.00107 -3.322381,3.68917 -5.744831,4.14135 -3.948177,0.92106 -8.513486,-1.36878 -9.733197,-5.32305 -1.054682,-3.04269 0.177256,-6.61183 2.780165,-8.46802 2.954657,-2.26559 7.375534,-2.25009 10.310622,0.0429 0.894445,0.61202 1.512054,1.517 2.193882,2.3275 0.863113,0.46836 1.460299,-0.56784 2.171337,-0.86976 0.728082,-0.48541 1.454221,-0.97374 2.184132,-1.45641 -1.563833,-2.16228 -3.502858,-4.14452 -5.995501,-5.20385 -1.962569,-0.87753 -4.133487,-1.29781 -6.281843,-1.21021 z m 25.26172,0.14063 c -4.09266,0.28122 -7.95134,2.71824 -9.91824,6.32415 -2.35129,4.10934 -2.02022,9.56366 0.841915,13.34246 2.831155,3.95137 8.131665,5.90028 12.845265,4.69584 4.82701,-1.08503 8.71226,-5.35176 9.36218,-10.25276 0.67657,-4.39157 -1.23263,-9.05676 -4.82772,-11.67929 -2.36461,-1.75932 -5.35587,-2.67905 -8.3034,-2.4304 z m 87.89648,0.32031 c -1.04557,0 -2.09114,0 -3.13672,0 -2.20565,5.02701 -4.41287,10.05333 -6.61914,15.08008 -2.18059,-4.86476 -4.36436,-9.72812 -6.54296,-14.59375 -0.64103,-0.78827 -1.73363,-0.33075 -2.58985,-0.45277 -1.01107,5.8e-4 -2.02213,0.002 -3.0332,0.002 3.16157,7.05132 6.32571,14.10148 9.48828,21.15235 -1.50919,3.43877 -3.01779,6.87779 -4.52734,10.3164 1.24913,1.09437 2.49557,2.19183 3.74218,3.28907 4.89606,-11.1734 9.82062,-22.33797 14.69727,-33.51758 0.31513,-0.74355 -0.41668,-1.45929 -1.16234,-1.29321 -0.10539,0.006 -0.21079,0.0119 -0.31618,0.0178 z m -71.79492,0.52539 c 0,5.14193 0,10.28385 0,15.42578 0.27852,0.69426 0.28621,1.47362 0.62891,2.15625 1.58202,4.1649 6.19703,6.62519 10.53906,6.29687 3.91774,-0.19559 7.77584,-2.63735 9.16507,-6.38353 0.61302,-1.54054 0.61827,-3.20776 0.58102,-4.83873 0,-4.39466 0,-8.78932 0,-13.18399 -1.64388,0 -3.28776,0 -4.93164,0 -0.0351,5.16937 0.0734,10.34487 -0.0631,15.51026 -0.40245,2.69183 -3.25316,4.42166 -5.85,4.19957 -2.58454,-0.0641 -5.21923,-2.25962 -5.11035,-4.91881 -0.0699,-4.71909 -0.007,-9.44074 -0.0293,-14.16094 0.26095,-0.81473 -0.47496,-0.63259 -1.0293,-0.63203 -1.30013,0 -2.60026,0 -3.90039,0 0,0.17643 0,0.35287 0,0.5293 z m 37.60156,3.72461 c 3.66585,0.19992 6.74233,3.4949 6.9253,7.12348 0.29986,3.49073 -2.01926,7.08023 -5.45184,7.98537 -3.14494,0.94307 -6.75648,-0.55501 -8.37173,-3.3978 -1.91791,-3.10444 -1.29025,-7.51754 1.51656,-9.88133 1.4527,-1.3108 3.43009,-1.98464 5.38171,-1.82972 z m -52.78515,0.16015 c 3.54098,-0.0597 6.73294,2.80374 7.32617,6.25 0.67976,3.41164 -1.16904,7.18358 -4.40996,8.54902 -3.03832,1.40507 -6.88744,0.30594 -8.84415,-2.37552 -2.30195,-2.94839 -2.01574,-7.52609 0.62584,-10.17131 1.36857,-1.43174 3.32237,-2.25388 5.3021,-2.25219 z"/>
                                </g>
                            </svg>
                        </div>
                        <div className={"dark:block hidden ml-2 mt-1"}>
                            <svg
                                width="20mm"
                                viewBox="0 0 127.22914 42.930099"
                                version="1.1"
                                id="svg1"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <defs
                                    id="defs1"/>
                                <g
                                    id="layer1"
                                    transform="translate(-71.045215,-136.04298)">
                                    <path
                                        id="path5"
                                        style={{opacity: 1, fill: "#ffffff", strokeWidth: 0.271314}}
                                        d="m 169.55859,136.57227 c 0,3.34114 0,6.68229 0,10.02343 -3.64384,-2.97109 -9.03383,-3.56317 -13.20995,-1.38285 -4.45036,2.14574 -7.22686,7.20497 -6.71021,12.10926 0.37354,4.63585 3.64458,8.85447 8.0242,10.40487 3.89052,1.44749 8.47464,0.73327 11.73539,-1.83816 2.99116,-2.29577 4.80307,-6.02011 4.75408,-9.79171 0.0392,-4.31115 -0.005,-8.62355 0.0139,-12.93539 0,-2.37292 0,-4.74583 0,-7.11875 -1.53581,0 -3.07162,0 -4.60743,0 0,0.17643 0,0.35286 0,0.5293 z m -85.99609,7.14648 c -4.799296,0.20059 -9.491305,3.09438 -11.448599,7.54401 -1.921844,4.05822 -1.187366,9.13683 1.686685,12.56286 3.484823,4.31576 9.776512,5.94694 14.959693,4.04564 2.529658,-0.84345 4.713598,-2.52375 6.325228,-4.63279 0.498008,-0.4781 0.853415,-0.9363 -0.0036,-1.21895 -1.134739,-0.76039 -2.26932,-1.52102 -3.404172,-2.28124 -1.342743,2.00107 -3.322381,3.68917 -5.744831,4.14135 -3.948177,0.92106 -8.513486,-1.36878 -9.733197,-5.32305 -1.054682,-3.04269 0.177256,-6.61183 2.780165,-8.46802 2.954657,-2.26559 7.375534,-2.25009 10.310622,0.0429 0.894445,0.61202 1.512054,1.517 2.193882,2.3275 0.863113,0.46836 1.460299,-0.56784 2.171337,-0.86976 0.728082,-0.48541 1.454221,-0.97374 2.184132,-1.45641 -1.563833,-2.16228 -3.502858,-4.14452 -5.995501,-5.20385 -1.962569,-0.87753 -4.133487,-1.29781 -6.281843,-1.21021 z m 25.26172,0.14063 c -4.09266,0.28122 -7.95134,2.71824 -9.91824,6.32415 -2.35129,4.10934 -2.02022,9.56366 0.841915,13.34246 2.831155,3.95137 8.131665,5.90028 12.845265,4.69584 4.82701,-1.08503 8.71226,-5.35176 9.36218,-10.25276 0.67657,-4.39157 -1.23263,-9.05676 -4.82772,-11.67929 -2.36461,-1.75932 -5.35587,-2.67905 -8.3034,-2.4304 z m 87.89648,0.32031 c -1.04557,0 -2.09114,0 -3.13672,0 -2.20565,5.02701 -4.41287,10.05333 -6.61914,15.08008 -2.18059,-4.86476 -4.36436,-9.72812 -6.54296,-14.59375 -0.64103,-0.78827 -1.73363,-0.33075 -2.58985,-0.45277 -1.01107,5.8e-4 -2.02213,0.002 -3.0332,0.002 3.16157,7.05132 6.32571,14.10148 9.48828,21.15235 -1.50919,3.43877 -3.01779,6.87779 -4.52734,10.3164 1.24913,1.09437 2.49557,2.19183 3.74218,3.28907 4.89606,-11.1734 9.82062,-22.33797 14.69727,-33.51758 0.31513,-0.74355 -0.41668,-1.45929 -1.16234,-1.29321 -0.10539,0.006 -0.21079,0.0119 -0.31618,0.0178 z m -71.79492,0.52539 c 0,5.14193 0,10.28385 0,15.42578 0.27852,0.69426 0.28621,1.47362 0.62891,2.15625 1.58202,4.1649 6.19703,6.62519 10.53906,6.29687 3.91774,-0.19559 7.77584,-2.63735 9.16507,-6.38353 0.61302,-1.54054 0.61827,-3.20776 0.58102,-4.83873 0,-4.39466 0,-8.78932 0,-13.18399 -1.64388,0 -3.28776,0 -4.93164,0 -0.0351,5.16937 0.0734,10.34487 -0.0631,15.51026 -0.40245,2.69183 -3.25316,4.42166 -5.85,4.19957 -2.58454,-0.0641 -5.21923,-2.25962 -5.11035,-4.91881 -0.0699,-4.71909 -0.007,-9.44074 -0.0293,-14.16094 0.26095,-0.81473 -0.47496,-0.63259 -1.0293,-0.63203 -1.30013,0 -2.60026,0 -3.90039,0 0,0.17643 0,0.35287 0,0.5293 z m 37.60156,3.72461 c 3.66585,0.19992 6.74233,3.4949 6.9253,7.12348 0.29986,3.49073 -2.01926,7.08023 -5.45184,7.98537 -3.14494,0.94307 -6.75648,-0.55501 -8.37173,-3.3978 -1.91791,-3.10444 -1.29025,-7.51754 1.51656,-9.88133 1.4527,-1.3108 3.43009,-1.98464 5.38171,-1.82972 z m -52.78515,0.16015 c 3.54098,-0.0597 6.73294,2.80374 7.32617,6.25 0.67976,3.41164 -1.16904,7.18358 -4.40996,8.54902 -3.03832,1.40507 -6.88744,0.30594 -8.84415,-2.37552 -2.30195,-2.94839 -2.01574,-7.52609 0.62584,-10.17131 1.36857,-1.43174 3.32237,-2.25388 5.3021,-2.25219 z"/>
                                </g>
                            </svg>
                        </div>
                    </div>
                </div>
            </SidebarHeader>
            <SidebarContent className="pb-0 mt-2 relative overflow-x-hidden" style={{ minHeight: 400 }}>
                <AnimatePresence custom={direction} mode="wait" initial={false}>
                    {!showChatList ? (
                        <motion.div
                            key="main"
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            custom={direction}
                            transition={{ duration: 0.35, ease: "easeInOut" }}
                            style={{ height: "100%" }}
                        >
                            {/* Hlavní sekce */}
                            <div className="px-3">
                                <div className="text-xs text-zinc-400 pt-1 pb-1 mb-2">Hlavní</div>
                                <SidebarMenu>
                                    {menuMain.map((item) =>
                                        item.label === "Chat" ? (
                                            <SidebarMenuItem key={item.label}>
                                                <SidebarMenuButton onClick={item.onClick}>
                                                    {item.icon && <item.icon className="w-4 h-4 mr-2" />}
                                                    <span>{item.label}</span>
                                                    {item.update && (
                                                        <Badge className={"text-xs"} variant="secondary">
                                                            v2.0
                                                        </Badge>
                                                    )}
                                                </SidebarMenuButton>
                                            </SidebarMenuItem>
                                        ) : item.children ? (
                                            <Collapsible key={item.label} asChild>
                                                <SidebarMenuItem>
                                                    <CollapsibleTrigger asChild>
                                                        <SidebarMenuButton
                                                            className={`${shouldDisableCollapsible(item) ? 'opacity-50' : ''}`}
                                                            onClick={shouldDisableCollapsible(item) ? (e) => handleCollapsibleClick(e, item) : undefined}
                                                        >
                                                            {item.icon && <item.icon className="w-4 h-4 mr-2" />}
                                                            <span className="flex items-center gap-2">
                                                                {item.label}
                                                                {shouldShowCrown(item) && (
                                                                    <Crown className="w-3 h-3 text-yellow-500" />
                                                                )}
                                                                {item.update && (
                                                                    <Badge>
                                                                        UPDATE
                                                                    </Badge>
                                                                )}
                                                            </span>
                                                            <ChevronRight className="ml-auto w-4 h-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                                        </SidebarMenuButton>
                                                    </CollapsibleTrigger>
                                                    <CollapsibleContent>
                                                        <SidebarMenuSub className="ml-6 mt-1 space-y-1">
                                                            {item.children.map((sub) => (
                                                                <SidebarMenuSubItem key={sub.label}>
                                                                    <SidebarMenuSubButton
                                                                        asChild
                                                                        className={`${sub.premium && !hasActiveSubscription ? 'opacity-50 relative cursor-not-allowed' : ''}`}
                                                                    >
                                                                        <a
                                                                            href={sub.url}
                                                                            className="flex items-center"
                                                                            onClick={(e) => {
                                                                                if (sub.premium && !hasActiveSubscription) {
                                                                                    handlePremiumFeatureClick(e, sub.url)
                                                                                    return
                                                                                }

                                                                                if (item.onClick) {
                                                                                    e.preventDefault()
                                                                                    item.onClick()
                                                                                    return;
                                                                                }

                                                                                e.preventDefault();
                                                                                navigate(sub.url)
                                                                                if(isMobile) {
                                                                                    setOpenMobile(false)
                                                                                }
                                                                            }}
                                                                        >
                                                                            {sub['new'] ? <div className={"absolute top-1 left-1 w-2 h-2 rounded-full bg-red-500"}></div> : <></>}
                                                                            {sub.icon && <sub.icon className="w-4 h-4 mr-2" />}
                                                                            <span className="flex items-center gap-2">
                                                                                {sub.label}
                                                                                {sub.premium && !hasActiveSubscription && (
                                                                                    <Crown className="w-3 h-3 text-yellow-500" />
                                                                                )}
                                                                            </span>
                                                                        </a>
                                                                    </SidebarMenuSubButton>
                                                                </SidebarMenuSubItem>
                                                            ))}
                                                        </SidebarMenuSub>
                                                    </CollapsibleContent>
                                                </SidebarMenuItem>
                                            </Collapsible>
                                        ) : (
                                            <SidebarMenuItem key={item.label}>
                                                <SidebarMenuButton
                                                    isActive={isActive(item.url)}
                                                    asChild
                                                    className={`${item.premium && !hasActiveSubscription ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <a
                                                        href={item.url}
                                                        onClick={(e) => {
                                                            if (item.premium && !hasActiveSubscription) {
                                                                handlePremiumFeatureClick(e, item.url)
                                                                return
                                                            }

                                                            if (item.onClick) {
                                                                e.preventDefault()
                                                                item.onClick()
                                                                return;
                                                            }

                                                            e.preventDefault();
                                                            navigate(item.url)

                                                            if(isMobile) {
                                                                setOpenMobile(false)
                                                            }
                                                        }}
                                                    >
                                                        {item.icon && <item.icon className="w-4 h-4 mr-2" />}
                                                        <span className="flex items-center gap-2">
                                                            {item.label}
                                                            {item.premium && !hasActiveSubscription && (
                                                                <Crown className="w-3 h-3 text-yellow-500" />
                                                            )}
                                                            {item.update && (
                                                                <Badge>
                                                                    UPDATE
                                                                </Badge>
                                                            )}
                                                        </span>
                                                    </a>
                                                </SidebarMenuButton>
                                            </SidebarMenuItem>
                                        )
                                    )}
                                </SidebarMenu>
                            </div>
                            <div className="px-3 pt-4">
                                <div className="text-xs text-zinc-400 pt-2 pb-1 mb-2">Další</div>
                                <SidebarMenu>
                                    {menuSecondary.map((item) => (
                                        <SidebarMenuItem key={item.label}>
                                            <SidebarMenuButton asChild>
                                                <a href={item.url} onClick={(e) => {
                                                    if (item.onClick) {
                                                        e.preventDefault()
                                                        item.onClick()
                                                        return;
                                                    }

                                                    e.preventDefault();
                                                    navigate(item.url)

                                                    if(isMobile) {
                                                        setOpenMobile(false)
                                                    }
                                                }}>
                                                    {item.icon && <item.icon className="w-4 h-4 mr-2" />}
                                                    <span>{item.label}</span>
                                                </a>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    ))}
                                </SidebarMenu>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="chatlist"
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            custom={direction}
                            transition={{ duration: 0.35, ease: "easeInOut" }}
                            style={{ height: "100%" }}
                        >
                            <ChatListSidebar onBack={handleBack} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </SidebarContent>
            <SidebarFooter>
                <NavUser />
            </SidebarFooter>
            <SidebarRail />
        </Sidebar>
    )
}
